'use client';

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';

interface BotStatus {
  running: boolean;
  lastAnalysisTime: number;
  totalAnalyses: number;
  lastDecision?: {
    action: string;
    symbol?: string;
    reason: string;
    confidence: number;
  };
  lastAction?: string;
  accountState: {
    totalBalance: number;
    availableMargin: number;
    usedMargin: number;
    unrealizedPnl: number;
    realizedPnl: number;
    positions: any[];
    trades: any[];
  };
  riskStatus: {
    tradingPaused: boolean;
    pauseReason: string;
    consecutiveLosses: number;
    drawdownPercent: number;
    dailyPnl: number;
    dailyPnlPercent: number;
  };
}

interface Statistics {
  totalTrades: number;
  winningTrades: number;
  losingTrades: number;
  winRate: number;
  totalPnl: number;
  roi: number;
  currentEquity: number;
  initialBalance: number;
}

interface TripleBotData {
  deepseek: { status: BotStatus; statistics: Statistics } | null;
  doubao: { status: BotStatus; statistics: Statistics } | null;
  qwen: { status: BotStatus; statistics: Statistics } | null;
  running: boolean;
}

export function TradingControl() {
  const { t } = useLanguage();

  // DeepSeek 配置
  const [deepseekApiKey, setDeepseekApiKey] = useState(process.env.NEXT_PUBLIC_DEEPSEEK_API_KEY || '');
  const [deepseekModelEndpoint, setDeepseekModelEndpoint] = useState(process.env.NEXT_PUBLIC_DEEPSEEK_MODEL_ENDPOINT || '');

  // 豆包配置
  const [doubaoApiKey, setDoubaoApiKey] = useState(process.env.NEXT_PUBLIC_DOUBAO_API_KEY || '');
  const [doubaoModel, setDoubaoModel] = useState(process.env.NEXT_PUBLIC_DOUBAO_MODEL || 'doubao-seed-1-6-251015');

  // 通义千问配置
  const [qwenApiKey, setQwenApiKey] = useState(process.env.NEXT_PUBLIC_QWEN_API_KEY || '');
  const [qwenModel, setQwenModel] = useState(process.env.NEXT_PUBLIC_QWEN_MODEL || 'qwen-max');

  const [initialBalance, setInitialBalance] = useState(Number(process.env.NEXT_PUBLIC_INITIAL_BALANCE) || 1000);
  const [tradingInterval, setTradingInterval] = useState(300); // 秒 (默认5分钟)

  // 三模型数据
  const [tripleBotData, setTripleBotData] = useState<TripleBotData>({
    deepseek: null,
    doubao: null,
    qwen: null,
    running: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 定期获取状态
  useEffect(() => {
    const interval = setInterval(fetchStatus, 3000);
    fetchStatus(); // 立即获取一次
    return () => clearInterval(interval);
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await fetch('/api/bot');
      const data = await response.json();

      setTripleBotData({
        deepseek: data.deepseek,
        doubao: data.doubao,
        qwen: data.qwen,
        running: data.running || false
      });
    } catch (err) {
      // 静默失败，不影响用户体验
    }
  };

  const startBot = async () => {
    // 验证必需的 API Key（三个都需要）
    if (!deepseekApiKey.trim()) {
      setError('Please enter DeepSeek API Key');
      return;
    }
    if (!doubaoApiKey.trim()) {
      setError('Please enter Doubao API Key');
      return;
    }
    if (!qwenApiKey.trim()) {
      setError('Please enter Qwen API Key');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/bot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'start',
          config: {
            deepseekApiKey,
            deepseekModelEndpoint,
            doubaoApiKey,
            doubaoModel,
            qwenApiKey,
            qwenModel,
            initialBalance,
            tradingInterval: tradingInterval * 1000, // 秒转毫秒
            enableTrading: true
          }
        })
      });

      const data = await response.json();

      if (data.success) {
        setTripleBotData({
          deepseek: data.deepseek,
          doubao: data.doubao,
          qwen: data.qwen,
          running: true
        });
        setError('');
        // 保存配置到 localStorage
        localStorage.setItem('deepseek_api_key', deepseekApiKey);
        localStorage.setItem('doubao_api_key', doubaoApiKey);
        localStorage.setItem('qwen_api_key', qwenApiKey);
      } else {
        setError(data.message || 'Failed to start bots');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const stopBot = async () => {
    setLoading(true);

    try {
      const response = await fetch('/api/bot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'stop' })
      });

      const data = await response.json();

      if (data.success) {
        setTripleBotData({
          deepseek: data.deepseek,
          doubao: data.doubao,
          qwen: data.qwen,
          running: false
        });
        // 清除 localStorage 中的 API Key
        localStorage.removeItem('deepseek_api_key');
        localStorage.removeItem('doubao_api_key');
        localStorage.removeItem('qwen_api_key');
      } else {
        setError(data.message || 'Failed to stop bots');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const resumeBot = async () => {
    try {
      const response = await fetch('/api/bot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'resume' })
      });

      const data = await response.json();
      if (data.success) {
        fetchStatus();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    }
  };

  return (
    <div className="space-y-6">
      {/* 控制面板 */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-6"
      >
        <h2 className="text-xl font-bold mb-4">🤖 AI Trading Bot - Triple Model Competition</h2>

        {!tripleBotData.running ? (
          <div className="space-y-4">
            {/* 三模型竞赛说明 */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded px-4 py-2">
              <p className="text-sm text-blue-400">
                ⚡ 竞赛模式：DeepSeek-V3、豆包、通义千问将同时运行，独立交易，实时对比性能表现！
              </p>
            </div>

            {/* DeepSeek 配置 */}
            <div className="bg-zinc-800/30 rounded p-4 space-y-3">
              <h3 className="text-md font-bold text-blue-400">🔵 DeepSeek-V3</h3>
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  API Key *
                </label>
                <input
                  type="password"
                  value={deepseekApiKey}
                  onChange={(e) => setDeepseekApiKey(e.target.value)}
                  placeholder="Enter DeepSeek API key"
                  className="w-full bg-black border border-zinc-700 rounded px-4 py-2 text-white focus:border-blue-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  Model Endpoint ID (Optional)
                </label>
                <input
                  type="text"
                  value={deepseekModelEndpoint}
                  onChange={(e) => setDeepseekModelEndpoint(e.target.value)}
                  placeholder="ep-20250120155516-xxxxx"
                  className="w-full bg-black border border-zinc-700 rounded px-4 py-2 text-white focus:border-blue-500 outline-none"
                />
              </div>
            </div>

            {/* 豆包配置 */}
            <div className="bg-zinc-800/30 rounded p-4 space-y-3">
              <h3 className="text-md font-bold text-purple-400">🟣 豆包 (Doubao)</h3>
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  API Key *
                </label>
                <input
                  type="password"
                  value={doubaoApiKey}
                  onChange={(e) => setDoubaoApiKey(e.target.value)}
                  placeholder="Enter Doubao API key"
                  className="w-full bg-black border border-zinc-700 rounded px-4 py-2 text-white focus:border-purple-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  Model ID (Optional)
                </label>
                <input
                  type="text"
                  value={doubaoModel}
                  onChange={(e) => setDoubaoModel(e.target.value)}
                  placeholder="doubao-seed-1-6-251015"
                  className="w-full bg-black border border-zinc-700 rounded px-4 py-2 text-white focus:border-purple-500 outline-none"
                />
              </div>
            </div>

            {/* 通义千问配置 */}
            <div className="bg-zinc-800/30 rounded p-4 space-y-3">
              <h3 className="text-md font-bold text-orange-400">🟠 通义千问 (Qwen)</h3>
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  API Key *
                </label>
                <input
                  type="password"
                  value={qwenApiKey}
                  onChange={(e) => setQwenApiKey(e.target.value)}
                  placeholder="Enter Qwen API key"
                  className="w-full bg-black border border-zinc-700 rounded px-4 py-2 text-white focus:border-orange-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  Model ID (Optional)
                </label>
                <input
                  type="text"
                  value={qwenModel}
                  onChange={(e) => setQwenModel(e.target.value)}
                  placeholder="qwen-max"
                  className="w-full bg-black border border-zinc-700 rounded px-4 py-2 text-white focus:border-orange-500 outline-none"
                />
              </div>
            </div>

            <p className="text-xs text-gray-500">
              Get your API keys from{' '}
              <a
                href="https://console.volcengine.com/ark"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:underline"
              >
                Volcengine Ark Console
              </a>
              {' / '}
              <a
                href="https://bailian.console.aliyun.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange-400 hover:underline"
              >
                Alibaba Cloud DashScope
              </a>
            </p>

            {/* 初始资金 */}
            <div>
              <label className="block text-sm text-gray-400 mb-2">
                Initial Balance (USDT)
              </label>
              <input
                type="number"
                value={initialBalance}
                onChange={(e) => setInitialBalance(Number(e.target.value))}
                min={100}
                max={100000}
                className="w-full bg-black border border-zinc-700 rounded px-4 py-2 text-white focus:border-blue-500 outline-none"
              />
            </div>

            {/* 交易间隔 */}
            <div>
              <label className="block text-sm text-gray-400 mb-2">
                Analysis Interval
              </label>
              <select
                value={tradingInterval}
                onChange={(e) => setTradingInterval(Number(e.target.value))}
                className="w-full bg-black border border-zinc-700 rounded px-4 py-2 text-white focus:border-blue-500 outline-none"
              >
                <option value={10}>10 seconds (Ultra Fast)</option>
                <option value={20}>20 seconds (Very Fast)</option>
                <option value={30}>30 seconds (Fast)</option>
                <option value={60}>1 minute</option>
                <option value={120}>2 minutes</option>
                <option value={300}>5 minutes (Recommended)</option>
                <option value={900}>15 minutes (Conservative)</option>
                <option value={3600}>1 hour (Long-term)</option>
              </select>
            </div>

            {/* 启动按钮 */}
            <button
              onClick={startBot}
              disabled={loading}
              className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white font-bold py-3 px-6 rounded transition-colors"
            >
              {loading ? 'Starting...' : '🚀 Start AI Trading Bot'}
            </button>

            {error && (
              <div className="bg-red-500/10 border border-red-500/50 rounded p-3 text-red-400 text-sm">
                {error}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {/* 运行状态 */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-green-400 font-bold">双模型竞赛运行中</span>
              </div>
            </div>

            {/* DeepSeek 状态 */}
            {tripleBotData.deepseek && (
              <div className="bg-blue-500/10 border border-blue-500/30 rounded p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="font-bold text-blue-400">🔵 DeepSeek-V3</h4>
                  <span className="text-xs text-gray-400">
                    Analysis #{tripleBotData.deepseek.status.totalAnalyses}
                  </span>
                </div>
                {tripleBotData.deepseek.status.lastDecision && (
                  <div className="text-sm">
                    <span className="text-gray-400">Latest:</span>{' '}
                    <span className="text-sky-400 font-semibold">
                      {tripleBotData.deepseek.status.lastDecision.action}
                    </span>
                    {tripleBotData.deepseek.status.lastDecision.symbol && (
                      <span className="text-gray-400"> - {tripleBotData.deepseek.status.lastDecision.symbol}</span>
                    )}
                    <span className="text-xs text-gray-500 ml-2">
                      ({(tripleBotData.deepseek.status.lastDecision.confidence * 100).toFixed(0)}%)
                    </span>
                  </div>
                )}
                <div className="text-xs text-gray-400">
                  Equity: ${tripleBotData.deepseek.status.accountState.totalBalance.toFixed(2)} |
                  PnL: <span className={tripleBotData.deepseek.status.accountState.realizedPnl >= 0 ? 'text-green-400' : 'text-red-400'}>
                    {tripleBotData.deepseek.status.accountState.realizedPnl >= 0 ? '+' : ''}
                    ${tripleBotData.deepseek.status.accountState.realizedPnl.toFixed(2)}
                  </span>
                </div>
              </div>
            )}

            {/* 豆包状态 */}
            {tripleBotData.doubao && (
              <div className="bg-purple-500/10 border border-purple-500/30 rounded p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="font-bold text-purple-400">🟣 豆包 (Doubao)</h4>
                  <span className="text-xs text-gray-400">
                    Analysis #{tripleBotData.doubao.status.totalAnalyses}
                  </span>
                </div>
                {tripleBotData.doubao.status.lastDecision && (
                  <div className="text-sm">
                    <span className="text-gray-400">Latest:</span>{' '}
                    <span className="text-sky-400 font-semibold">
                      {tripleBotData.doubao.status.lastDecision.action}
                    </span>
                    {tripleBotData.doubao.status.lastDecision.symbol && (
                      <span className="text-gray-400"> - {tripleBotData.doubao.status.lastDecision.symbol}</span>
                    )}
                    <span className="text-xs text-gray-500 ml-2">
                      ({(tripleBotData.doubao.status.lastDecision.confidence * 100).toFixed(0)}%)
                    </span>
                  </div>
                )}
                <div className="text-xs text-gray-400">
                  Equity: ${tripleBotData.doubao.status.accountState.totalBalance.toFixed(2)} |
                  PnL: <span className={tripleBotData.doubao.status.accountState.realizedPnl >= 0 ? 'text-green-400' : 'text-red-400'}>
                    {tripleBotData.doubao.status.accountState.realizedPnl >= 0 ? '+' : ''}
                    ${tripleBotData.doubao.status.accountState.realizedPnl.toFixed(2)}
                  </span>
                </div>
              </div>
            )}

            {/* 通义千问状态 */}
            {tripleBotData.qwen && (
              <div className="bg-orange-500/10 border border-orange-500/30 rounded p-4">
                <h3 className="text-md font-bold text-orange-400 mb-2">🟠 通义千问 Status</h3>
                <div className="text-sm space-y-1">
                  <div className="text-gray-300">
                    Analysis #{tripleBotData.qwen.status.totalAnalyses}
                  </div>
                {tripleBotData.qwen.status.lastDecision && (
                  <div className="text-gray-300">
                    Last Decision:{' '}
                    <span className="font-bold text-orange-400">
                      {tripleBotData.qwen.status.lastDecision.action}
                    </span>
                    {tripleBotData.qwen.status.lastDecision.symbol && (
                      <span className="text-gray-400"> - {tripleBotData.qwen.status.lastDecision.symbol}</span>
                    )}
                    <span className="text-gray-400">
                      {' '}({(tripleBotData.qwen.status.lastDecision.confidence * 100).toFixed(0)}%)
                    </span>
                  </div>
                )}
                <div className="text-gray-300">
                  Equity: ${tripleBotData.qwen.status.accountState.totalBalance.toFixed(2)} |
                  PnL: <span className={tripleBotData.qwen.status.accountState.realizedPnl >= 0 ? 'text-green-400' : 'text-red-400'}>
                    {tripleBotData.qwen.status.accountState.realizedPnl >= 0 ? '+' : ''}
                    ${tripleBotData.qwen.status.accountState.realizedPnl.toFixed(2)}
                  </span>
                </div>
                </div>
              </div>
            )}

            {/* 停止按钮 */}
            <button
              onClick={stopBot}
              disabled={loading}
              className="w-full bg-red-600 hover:bg-red-700 disabled:bg-gray-600 text-white font-bold py-3 px-6 rounded transition-colors"
            >
              {loading ? 'Stopping...' : '⏹️ Stop All Three Bots'}
            </button>
          </div>
        )}
      </motion.div>

      {/* 三模型统计对比 */}
      {(tripleBotData.deepseek || tripleBotData.doubao || tripleBotData.qwen) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-6"
        >
          <h3 className="text-lg font-bold mb-4">📊 Performance Comparison</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* DeepSeek 统计 */}
            {tripleBotData.deepseek && (
              <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-4">
                <h4 className="font-bold text-blue-400 mb-3">🔵 DeepSeek-V3</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs text-gray-400">Equity</div>
                    <div className="text-lg font-bold">
                      ${tripleBotData.deepseek.statistics.currentEquity.toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">ROI</div>
                    <div className={`text-lg font-bold ${tripleBotData.deepseek.statistics.roi >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {tripleBotData.deepseek.statistics.roi >= 0 ? '+' : ''}{tripleBotData.deepseek.statistics.roi.toFixed(2)}%
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">Win Rate</div>
                    <div className="text-lg font-bold">
                      {tripleBotData.deepseek.statistics.winRate.toFixed(1)}%
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">Trades</div>
                    <div className="text-lg font-bold">
                      {tripleBotData.deepseek.statistics.totalTrades}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 豆包统计 */}
            {tripleBotData.doubao && (
              <div className="bg-purple-500/5 border border-purple-500/20 rounded-lg p-4">
                <h4 className="font-bold text-purple-400 mb-3">🟣 豆包 (Doubao)</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs text-gray-400">Equity</div>
                    <div className="text-lg font-bold">
                      ${tripleBotData.doubao.statistics.currentEquity.toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">ROI</div>
                    <div className={`text-lg font-bold ${tripleBotData.doubao.statistics.roi >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {tripleBotData.doubao.statistics.roi >= 0 ? '+' : ''}{tripleBotData.doubao.statistics.roi.toFixed(2)}%
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">Win Rate</div>
                    <div className="text-lg font-bold">
                      {tripleBotData.doubao.statistics.winRate.toFixed(1)}%
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">Trades</div>
                    <div className="text-lg font-bold">
                      {tripleBotData.doubao.statistics.totalTrades}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 通义千问统计 */}
            {tripleBotData.qwen && (
              <div className="bg-orange-500/5 border border-orange-500/20 rounded-lg p-4">
                <h4 className="font-bold text-orange-400 mb-3">🟠 通义千问 (Qwen)</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs text-gray-400">Equity</div>
                    <div className="text-lg font-bold">
                      ${tripleBotData.qwen.statistics.currentEquity.toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">ROI</div>
                    <div className={`text-lg font-bold ${tripleBotData.qwen.statistics.roi >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {tripleBotData.qwen.statistics.roi >= 0 ? '+' : ''}{tripleBotData.qwen.statistics.roi.toFixed(2)}%
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">Win Rate</div>
                    <div className="text-lg font-bold">
                      {tripleBotData.qwen.statistics.winRate.toFixed(1)}%
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">Trades</div>
                    <div className="text-lg font-bold">
                      {tripleBotData.qwen.statistics.totalTrades}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
}
